import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { CountdownTimer } from './CountdownTimer';
import { QueueVideo } from './QueueVideo';
import { shopVar } from '@/lib/shopStyles';
import { RiLoopRightFill, RiNotification2Fill } from '@remixicon/react';
import { useShop } from '@/providers/ShopProvider';
import type { QueueResponse } from '@/hooks/useQueue';

import * as m from "@/paraglide/messages.js"

interface QueueOverlayProps {
  queueStatus: QueueResponse;
  onNotificationsEnabled: () => void;
  notificationsEnabled: boolean;
}

export function QueueOverlay({ queueStatus, onNotificationsEnabled, notificationsEnabled }: QueueOverlayProps) {
  const { shop } = useShop();
  const video = shop.queueVideo ?? null;

  if (queueStatus.status === 'converted') {
    const startNewOrder = () => {
      localStorage.removeItem('queueSessionId');
      localStorage.removeItem(`cart-${shop.slug}`);
      window.location.reload();
    };

    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: shopVar('--shop-textColor') }}>
            {m.queue_already_purchased()}
          </h2>
          <p className="text-[var(--shop-textColor)]/70">
            {m.queue_already_purchased_description()}
          </p>
          <button
            onClick={startNewOrder}
            className="mt-6 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--shop-ctaButtonColor)]"
          >
            {m.queue_start_new_order()}
          </button>
        </div>
      </div>
    );
  }

  if (queueStatus.status !== 'not_open' && queueStatus.status !== 'queued') {
    return null;
  }

  const saleStartsAt = new Date(queueStatus.saleStartsAt);
  const targetTimestamp = Math.floor(saleStartsAt.getTime() / 1000);

  return (
    <SaleCountdownOrQueue
      targetTimestamp={targetTimestamp}
      saleStartsAt={saleStartsAt}
      video={video}
      queueStatus={queueStatus}
      onNotificationsEnabled={onNotificationsEnabled}
      notificationsEnabled={notificationsEnabled}
    />
  );
}

function SaleCountdownOrQueue({
  targetTimestamp,
  saleStartsAt,
  video,
  queueStatus,
  onNotificationsEnabled,
  notificationsEnabled,
}: {
  targetTimestamp: number;
  saleStartsAt: Date;
  video: { url: string; duration: number } | null;
  queueStatus: QueueResponse;
  onNotificationsEnabled: () => void;
  notificationsEnabled: boolean;
}) {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    Math.max(0, targetTimestamp - Math.floor(Date.now() / 1000))
  );
  const [videoEnded, setVideoEnded] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsRemaining(Math.max(0, targetTimestamp - Math.floor(Date.now() / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetTimestamp]);

  const saleHasStarted = secondsRemaining <= 0;
  const isWithinOneHour = secondsRemaining <= 3600;
  const showVideo = video && !videoEnded && !saleHasStarted && secondsRemaining <= video.duration;

  // Sale hasn't started yet — show countdown
  if (!saleHasStarted) {
    return (
      <>
        {showVideo && (
          <QueueVideo
            video={video}
            targetTimestamp={targetTimestamp}
            onVideoEnd={() => setVideoEnded(true)}
          />
        )}

        {!showVideo && (
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold" style={{ color: shopVar('--shop-textColor') }}>
                {m.queue_not_started()}
              </h2>
              <p className="text-[var(--shop-textColor)]/70 py-4">
                {m.queue_not_started_description()}
              </p>
              {isWithinOneHour ? (
                <>
                  <p className="text-[var(--shop-textColor)]/70 mb-4">{m.queue_starts_in()}</p>
                  <CountdownTimer targetTimestamp={targetTimestamp} />
                </>
              ) : (
                <>
                  <p className="text-[var(--shop-textColor)]/70 mb-2">{m.queue_starts_at()}</p>
                  <p className="text-xl font-semibold" style={{ color: shopVar('--shop-textColor') }}>
                    {format(saleStartsAt, 'MMMM d, yyyy HH:mm')}
                  </p>
                </>
              )}
              <p className="text-sm text-[var(--shop-textColor)]/50 mt-6">
                {m.queue_keep_tab_open()}
              </p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Sale has started and user is queued — show waiting room
  if (queueStatus.status === 'queued') {
    return (
      <div className="p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: shopVar('--shop-textColor') }}>
            {m.queue_your_in_line()}
          </h2>
          <p className="text-[var(--shop-textColor)]/70">
            {m.queue_please_wait()}
          </p>

          {(queueStatus.peopleAhead !== undefined || queueStatus.estimatedWaitSeconds !== undefined) && (
            <div className="mt-6 space-y-2">
              {queueStatus.peopleAhead !== undefined && (
                <p className="text-lg font-semibold" style={{ color: shopVar('--shop-textColor') }}>
                  {m.queue_people_ahead({ count: queueStatus.peopleAhead })}
                </p>
              )}
              {queueStatus.estimatedWaitSeconds !== undefined && (
                <p className="text-[var(--shop-textColor)]/70">
                  {m.queue_estimated_wait({ minutes: Math.max(1, Math.ceil(queueStatus.estimatedWaitSeconds / 60)) })}
                </p>
              )}
            </div>
          )}

          <div className='flex items-center justify-center mt-4 mx-auto'>
            <p className="text-sm text-[var(--shop-textColor)]/50">
              {m.queue_refresh_instruction()}
            </p>
            <div className="[animation:spin_2s_linear_infinite] h-4 w-4 ml-4 text-[var(--shop-textColor)]/50">
              <RiLoopRightFill className="h-4 w-4" />
            </div>
          </div>
          <p className="text-sm text-[var(--shop-textColor)]/50 mt-4">
            {m.queue_keep_tab_open()}
          </p>
        </div>
        {!notificationsEnabled && (
          <div className="text-center mt-8 pt-8 border-t border-[var(--shop-borderColor)]">
            <button
              onClick={onNotificationsEnabled}
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--shop-ctaButtonColor)]"
            >
              <RiNotification2Fill className="mr-2 h-5 w-5" />
              {m.queue_notify_cta()}
            </button>
            <p className="mt-2 text-sm text-[var(--shop-textColor)]/50">
              {m.queue_notify_instruction()}
            </p>
          </div>
        )}
      </div>
    );
  }

  return null;
}
